import React, {useContext, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import {withRouter} from 'react-router';
import {AuthContext} from '../../../context/auth';
import {useLocation} from 'react-router-dom';
import {USER_ACCESS_LEVEL, PRODUCT_KEY, FEATURE_KEY} from '../../../utils/constants';
import { isProductEnabled, isFeatureEnabled } from '../../../utils/productAccess';
import WorkspaceNav from './WorkspaceNav.comp';
import { PARTNER_WORKSPACE_NAV } from '../../../config/workspaceNav/partnerWorkspace.nav';
import { resolveSolutions } from '../../../config/workspaceNav/solutions';
import { useTutorial } from '../../../components/TutorialSystem';
import { getNationalDashboardCountriesService } from '../../../services/dynamicDashboard.service';
import { useNationalSelection } from '../../../context/DashboardContext';

// Driven by the workspace branding (falls back to the DQ orange). See
// BrandingProvider, which sets --brand-primary from DQ_InsiderProfile.branding.
const ORANGE = 'var(--brand-primary, #FF6900)';
const GREY = 'var(--brand-text-muted, #A5A5A5)';

const useStyles = makeStyles(() => ({
    item: {
        width: '92%',
        margin: '2px 10px',
        borderRadius: '10px',
        padding: '10px 12px',
        color: GREY,
        '&:hover': {backgroundColor: 'rgba(237,102,40,0.08)'},
    },
    itemCollapsed: {
        width: '65%',
    },
    itemActive: {
        backgroundColor: ORANGE,
        color: '#ffffff',
        '&:hover': {backgroundColor: ORANGE},
    },
    /*
     * Levels 2 and 3 are separated by indent AND by type weight. Indent alone
     * was not enough once a three-level menu existed: identical 14px/600 text at
     * two indents reads as one long list, and the sidebar became a wall of rows.
     */
    subItem: {
        width: 'calc(100% - 34px)',
        margin: '1px 10px 1px 24px',
        borderRadius: '8px',
        padding: '7px 12px',
        color: GREY,
        '&:hover': {backgroundColor: 'rgba(237,102,40,0.08)'},
    },
    // Level 3 — Solutions > COS Children Assessment > Preview.
    subItem2: {
        width: 'calc(100% - 48px)',
        margin: '1px 10px 1px 38px',
        borderRadius: '8px',
        padding: '5px 12px',
        color: GREY,
        '&:hover': {backgroundColor: 'rgba(237,102,40,0.08)'},
    },
    subItemActive: {
        backgroundColor: ORANGE,
        color: '#ffffff',
        '&:hover': {backgroundColor: ORANGE},
    },
    icon: {minWidth: 'unset', marginRight: '12px'},
    label: {
        '& .MuiTypography-root': {fontSize: '14px', fontWeight: 600},
    },
    labelSub: {
        '& .MuiTypography-root': {fontSize: '13.5px', fontWeight: 500},
    },
    labelSub2: {
        '& .MuiTypography-root': {fontSize: '13px', fontWeight: 400},
    },
    divider: {margin: '4px 10px'},
}));

const MainList = (props) => {
    const {history, isDrawerOpen} = props;
    const classes = useStyles();
    const location = useLocation();
    const {authUser} = useContext(AuthContext);

    const { isActive: isTutorialActive, currentStep: tutorialStep } = useTutorial();

    /**
     * The tutorial points at sub-items inside My Programs. A highlighted element
     * inside a collapsed group cannot be seen, so the group is forced open for
     * the duration of those steps.
     */
    const programSubTargets = ['#sidebar-products-link', '#sidebar-assessment-list-link'];
    const forceOpenCodes = !isTutorialActive || !tutorialStep ? []
        : programSubTargets.includes(tutorialStep.target) ? ['PW_MP']
        : tutorialStep.target === '#sidebar-opportunity-pipeline-link' ? ['PW_GO']
        : [];

    const hasLearningPrograms = isProductEnabled(authUser, PRODUCT_KEY.LEARNING_PROGRAM);
    const hasAssessments = isProductEnabled(authUser, PRODUCT_KEY.ASSESSMENT)
        || isProductEnabled(authUser, PRODUCT_KEY.CPC_INDEX_ASSESSMENT);
    const hasAnyProgramAccess = hasLearningPrograms || hasAssessments;
    const insightsPath = authUser?.access_level === USER_ACCESS_LEVEL.INDIVIDUAL_ACCESS_DQ_INSIDER_PORTAL
        || isFeatureEnabled(authUser, FEATURE_KEY.INDIVIDUAL_DASHBOARD)
        ? '/dq_pro/individual-dashboard/score-overview'
        : authUser?.access_level === USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL
            || isFeatureEnabled(authUser, FEATURE_KEY.PARTNER_DASHBOARD)
            ? '/dq_pro/partner-dashboard/overview'
            : '/dq_pro/insights';

    const isActive = path => location.pathname === path || location.pathname.startsWith(path + '/');

    /**
     * National Dashboard — one per partner × country (6 Oct 2026). The menu lists
     * the partner's countries as children of the row, like the school menu's
     * classes; fetched only when the workspace has the feature. Failure resolves
     * to [] — the row then stays a single link, as before. The chosen country is
     * held in DashboardContext (not the URL), so the rows select rather than link.
     */
    const { nationalCountry, selectNationalCountry } = useNationalSelection();
    const [nationalRows, setNationalRows] = useState([]);
    const partnerId = authUser?.dq_insider_id;
    const canSeeNationalDashboard = isFeatureEnabled(authUser, FEATURE_KEY.NATIONAL_DASHBOARD);
    useEffect(() => {
        let cancelled = false;
        if (!partnerId || !canSeeNationalDashboard) {
            setNationalRows([]);
            return undefined;
        }
        getNationalDashboardCountriesService()
            .then((res) => { if (!cancelled) setNationalRows(Array.isArray(res?.data) ? res.data : []); })
            .catch(() => { if (!cancelled) setNationalRows([]); });
        return () => { cancelled = true; };
    }, [partnerId, canSeeNationalDashboard]);

    /**
     * Everything the nav config's `visibleWhen` and `path` resolvers read.
     *
     * Built here rather than inside the config so the gates stay expressed in
     * the same values this sidebar has always used — the restructure must not
     * quietly change who sees what.
     */
    const navCtx = {
        authUser,
        insightsPath,
        hasLearningPrograms,
        hasAssessments,
        hasAnyProgramAccess,
        // Consumed by partnerWorkspace.nav.js — PW_IN_ND builds one child per country,
        // each selecting its country; `nationalCountry` lights the selected one.
        nationalRows,
        nationalCountry,
        selectNationalCountry,
    };

    /**
     * Unlocked solutions, resolved from the session on the client.
     *
     * Not yet used as a gate — see SOLUTION_GATING_READY. Passed through now so
     * that turning gating on is a one-line change rather than a refactor.
     */
    const solutions = resolveSolutions(authUser);



    return (
        <div style={{display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden'}}>
            <List style={{flexGrow: 1, paddingTop: '8px', overflowY: 'auto'}}>
                {/*
                  * The whole menu comes from the PbDQ Navigation Bar sheet, via
                  * config rather than JSX. Adding or moving an item is a row in
                  * partnerWorkspace.nav.js, and the other four workspaces reuse
                  * this same renderer with their own tree.
                  *
                  * Routes and gates are unchanged from the hand-written version
                  * this replaces — the sheet restructures the menu, it does not
                  * grant anyone new access.
                  */}
                <WorkspaceNav
                    items={PARTNER_WORKSPACE_NAV}
                    ctx={navCtx}
                    solutions={solutions}
                    classes={classes}
                    isDrawerOpen={isDrawerOpen}
                    currentPath={location.pathname}
                    isActive={isActive}
                    onNavigate={(path) => history.push(path)}
                    forceOpenCodes={forceOpenCodes}
                />
            </List>

            {/*
              * No Dark Mode row here.
              *
              * The sheet's Partner sidebar is exactly eight rows — Home,
              * Solutions, Growth Opportunities, My Programs, LearnCreator,
              * Insights, My Schools, Support — and Dark Mode is not one of them.
              * Row 40 puts the toggle inside Settings > Advanced Settings, which
              * is where it now lives.
              *
              * Removing it also gives the three-level menu back the vertical
              * space it was taking at the bottom.
              */}
        </div>
    );
};

export default withRouter(MainList);
